/**
 * Theme tokens resolved from PrimeNG CSS custom properties for ECharts options.
 *
 * @example
 * const tokens = resolveEchartsThemeTokens();
 * // tokens.palette[0] === resolved --p-primary-500 (or fallback)
 */
export type EchartsThemeTokens = {
  /** Series / slice color palette derived from PrimeNG ramps. */
  palette: readonly string[];
  /**
   * High-contrast categorical fills (no primary as the first slice). Consecutive
   * items sit far apart on the hue wheel so neighbouring pie slices stay
   * distinguishable — electric status hues + steel + spaced primary tints.
   */
  categoricalPalette: readonly string[];
  /**
   * Sequential choropleth ramp from the primary blue ramp (never exact
   * `--p-primary-500` as the sole stop). Pale→deep in light mode, deep→bright
   * in dark mode so density stays readable on both surfaces while staying
   * distinct from the categorical pie palette.
   */
  choroplethRamp: readonly string[];
  /** Default body text (`--p-text-color`). */
  textColor: string;
  /** Muted / secondary text (`--p-text-muted-color`). */
  mutedColor: string;
  /** Content border / axis line (`--p-content-border-color`). */
  borderColor: string;
  /** Split-line / grid line color (semi-transparent border). */
  splitLineColor: string;
  /** Tooltip background (`--p-content-background`). */
  tooltipBackground: string;
  /** Tooltip border. */
  tooltipBorder: string;
};

/**
 * Literal fallbacks used when CSS custom properties are unavailable
 * (jsdom unit tests, SSR without styles). Match Indutrust sky `#1fa8f5` +
 * electric status / steel categorical accents.
 */
const FALLBACK_PALETTE: readonly string[] = [
  '#1fa8f5',
  '#0873b6',
  '#59b9ff',
  '#7a5af8',
  '#526980',
  '#f04438',
  '#12b76a',
  '#8ed3ff',
];

const FALLBACK_TEXT = '#334155';
const FALLBACK_MUTED = '#64748b';
const FALLBACK_BORDER = '#e2e8f0';
const FALLBACK_TOOLTIP_BG = '#ffffff';

const FALLBACK_CATEGORICAL: readonly string[] = [
  '#7a5af8',
  '#526980',
  '#f04438',
  '#12b76a',
  '#59b9ff',
  '#fb6514',
  '#0873b6',
  '#f79009',
];

const FALLBACK_CHOROPLETH_LIGHT: readonly string[] = [
  '#bce3ff',
  '#59b9ff',
  '#0873b6',
  '#10507b',
];

const FALLBACK_CHOROPLETH_DARK: readonly string[] = [
  '#0c6096',
  '#0b8de0',
  '#8ed3ff',
  '#d9efff',
];

const PALETTE_VARS = [
  '--p-primary-500',
  '--p-primary-700',
  '--p-primary-400',
  '--p-purple-500',
  '--p-surface-500',
  '--p-red-500',
  '--p-green-500',
  '--p-primary-300',
] as const;

/** Spaced electric status + steel + primary so neighbouring slices contrast. */
const CATEGORICAL_PALETTE_VARS = [
  '--p-purple-500',
  '--p-surface-500',
  '--p-red-500',
  '--p-green-500',
  '--p-primary-400',
  '--p-orange-500',
  '--p-primary-700',
  '--p-yellow-500',
] as const;

const CHOROPLETH_LIGHT_VARS = [
  '--p-primary-200',
  '--p-primary-400',
  '--p-primary-700',
  '--p-primary-900',
] as const;

const CHOROPLETH_DARK_VARS = [
  '--p-primary-800',
  '--p-primary-600',
  '--p-primary-300',
  '--p-primary-100',
] as const;
/**
 * Reads CSS custom properties as colors a canvas will actually accept.
 *
 * PrimeNG 22 emits its text and border tokens as nested `light-dark()`
 * functions, and `getPropertyValue` hands that function back verbatim. Assigning
 * it to `ctx.fillStyle` is **silently rejected** — the canvas keeps whatever
 * fill was last set, so an unresolved axis label renders as ghost text rather
 * than as an obviously wrong color, which is why this failed quietly for as
 * long as it did.
 *
 * Round-tripping each value through a probe element's `color` makes the browser
 * do the resolution, and because the probe inherits `color-scheme` from the
 * document it lands on the correct light/dark branch.
 */
function createCssColorReader(): {
  dispose: () => void;
  read: (name: string, fallback: string) => string;
} {
  if (typeof document === 'undefined') {
    return {
      dispose: (): void => undefined,
      read: (_name, fallback) => fallback,
    };
  }

  const root = getComputedStyle(document.documentElement);
  const probe = document.createElement('span');
  probe.style.display = 'none';
  document.body.append(probe);

  return {
    dispose: (): void => probe.remove(),
    read: (name, fallback): string => {
      const raw = root.getPropertyValue(name).trim();
      if (raw.length === 0) {
        return fallback;
      }

      probe.style.color = '';
      probe.style.color = raw;
      // An unparseable value leaves the inline style untouched, in which case
      // the computed color is the inherited one — not this token.
      if (probe.style.color.length === 0) {
        return fallback;
      }

      const resolved = getComputedStyle(probe).color.trim();
      return resolved.length > 0 ? resolved : fallback;
    },
  };
}

/**
 * Resolves PrimeNG theme tokens into concrete colors for ECharts options.
 *
 * Callers that need dark-mode reactivity should re-invoke this inside a
 * `computed()` / `effect()` that also reads `ThemeStore.isDarkTheme()` so the
 * options bag rebuilds when `.app-dark` flips.
 *
 * Results are cached per light/dark scheme so dashboard cards that rebuild
 * options on every filter change do not append a CSS probe to `document.body`
 * on each read.
 *
 * @returns Resolved {@link EchartsThemeTokens}.
 * @example
 * const { palette, textColor } = resolveEchartsThemeTokens();
 */
let cachedThemeTokens: {
  scheme: 'dark' | 'light';
  tokens: EchartsThemeTokens;
} | null = null;

function resolveColorScheme(): 'dark' | 'light' {
  if (typeof document === 'undefined') {
    return 'light';
  }
  return document.documentElement.classList.contains('app-dark')
    ? 'dark'
    : 'light';
}

/**
 * Drops the theme-token cache. Specs that flip `.app-dark` without going
 * through {@link resolveEchartsThemeTokens} should call this between cases.
 */
export function clearEchartsThemeTokenCache(): void {
  cachedThemeTokens = null;
}

export function resolveEchartsThemeTokens(): EchartsThemeTokens {
  const scheme = resolveColorScheme();
  if (cachedThemeTokens?.scheme === scheme) {
    return cachedThemeTokens.tokens;
  }

  const reader = createCssColorReader();

  try {
    const palette = PALETTE_VARS.map((cssVar, index) =>
      reader.read(cssVar, FALLBACK_PALETTE[index] ?? FALLBACK_PALETTE[0])
    );
    const categoricalPalette = CATEGORICAL_PALETTE_VARS.map((cssVar, index) =>
      reader.read(
        cssVar,
        FALLBACK_CATEGORICAL[index] ?? FALLBACK_CATEGORICAL[0]
      )
    );
    const choroplethVars =
      scheme === 'dark' ? CHOROPLETH_DARK_VARS : CHOROPLETH_LIGHT_VARS;
    const choroplethFallback =
      scheme === 'dark' ? FALLBACK_CHOROPLETH_DARK : FALLBACK_CHOROPLETH_LIGHT;
    const choroplethRamp = choroplethVars.map((cssVar, index) =>
      reader.read(cssVar, choroplethFallback[index] ?? choroplethFallback[0])
    );
    const borderColor = reader.read(
      '--p-content-border-color',
      FALLBACK_BORDER
    );

    const tokens: EchartsThemeTokens = {
      palette,
      categoricalPalette,
      choroplethRamp,
      textColor: reader.read('--p-text-color', FALLBACK_TEXT),
      mutedColor: reader.read('--p-text-muted-color', FALLBACK_MUTED),
      borderColor,
      splitLineColor: borderColor,
      tooltipBackground: reader.read(
        '--p-content-background',
        FALLBACK_TOOLTIP_BG
      ),
      tooltipBorder: borderColor,
    };
    cachedThemeTokens = { scheme, tokens };
    return tokens;
  } finally {
    reader.dispose();
  }
}
